import React, { useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  List,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Select,
  SimpleGrid,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tooltip,
  Tr,
} from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDebounceValue } from 'usehooks-ts';
import { toast } from 'sonner';
import {
  ArrowDown,
  ArrowUp,
  Briefcase,
  Buildings,
  Copy,
  CurrencyCircleDollar,
  DownloadSimple,
  FileText,
  Globe,
  Coins,
  Key,
  ListBullets,
  Lock,
  MagnifyingGlass,
  Package,
  PencilSimple,
  Plus,
  Question,
  Rows,
  Trash,
} from 'phosphor-react';
import { useMonthFilter } from '../../../../hooks/useMonthFilter';
import { MonthNavigator } from '../../../../components/shared/MonthNavigator';
import { ReturnButton } from '../../../../components/buttons/ReturnButton';
import { GlassPageBg } from '../../../../components/glass/GlassPageBg';
import { GlassCard } from '../../../../components/glass/GlassCard';
import { Tour, TourPasso, useTour } from '../../../../components/Tour';
import { useAuth } from '../../../../providers/auth';
import { IEmpresaSn, IFatura, TELA_FATURAS_PATH, mensagemErro, snApi } from './api';
import { SITUACOES, brl, cnpjFmt, dataBr } from './regras';

const TIPOS: Record<IFatura['tipo'], string> = {
  servico: 'Serviço',
  revenda: 'Revenda de mercadoria',
  industrializacao: 'Venda de industrializado',
  locacao: 'Locação sem ISS',
};

const COR_TIPO: Record<IFatura['tipo'], string> = {
  servico: 'blue',
  revenda: 'purple',
  industrializacao: 'purple',
  locacao: 'teal',
};

const PASSOS: TourPasso[] = [
  {
    alvo: 'mes',
    titulo: 'Mês da receita',
    texto: 'A fatura entra na apuração do mês escolhido aqui (regime de competência do Simples).',
  },
  {
    alvo: 'kpis',
    titulo: 'Resumo do mês',
    texto:
      'Total lançado comparado com o mês anterior, quantidade de faturas e empresas, e o valor por tipo. Clique num tipo para filtrar a lista.',
  },
  {
    alvo: 'copiar',
    titulo: 'Copiar do mês anterior',
    texto:
      'Receita recorrente (aluguel, mensalidade): marque as faturas do mês anterior e elas são lançadas de novo neste mês, no mesmo dia. As que já existem aqui vêm desmarcadas.',
  },
  {
    alvo: 'nova',
    titulo: 'Lançar fatura',
    texto:
      'Use para toda receita sem XML: fatura de locação, recibo, serviço sem NFS-e. Ela soma na receita do mês e o DAS é recalculado na hora.',
  },
  {
    alvo: 'tipo',
    titulo: 'Tipo e item da LC 116',
    texto:
      'O tipo decide o anexo: serviço vai pelo item da LC 116 (e Fator R quando couber); revenda Anexo I; industrializado Anexo II; locação de bens móveis Anexo III sem ISS. ISS retido e ST tiram o tributo do DAS.',
  },
  {
    alvo: 'lista',
    titulo: 'Conferência',
    texto:
      'Veja em lista ou agrupado por empresa (com subtotal e a situação da apuração). Se a apuração já estava aprovada, a aprovação cai e precisa aprovar de novo. Mês transmitido fica travado (cadeado): só por retificação. Exportar CSV baixa o que está filtrado.',
  },
];

const VAZIA = (empresaId: number, competencia: string): IFatura => ({
  empresa_id: empresaId,
  competencia,
  data: competencia,
  tipo: 'servico',
  descricao: '',
  numero_documento: null,
  tomador_doc: null,
  tomador_nome: null,
  valor: 0,
  item_lc116: null,
  iss_retido: false,
  municipio_ibge: null,
  icms_st: false,
  monofasico: false,
  exterior: false,
  observacao: null,
});

/** Busca de empresa do Simples (carteira do usuário). */
function EmpresaBusca({
  competencia,
  valor,
  onChange,
  isDisabled,
}: {
  competencia: string;
  valor: IEmpresaSn | null;
  onChange: (e: IEmpresaSn | null) => void;
  isDisabled?: boolean;
}) {
  const [texto, setTexto] = useState('');
  const [termo] = useDebounceValue(texto, 350);
  const { data, isFetching } = useQuery({
    queryKey: ['sn-empresas-busca', competencia, termo],
    queryFn: () => snApi.listar({ competencia, search: termo || undefined, size: 15 }),
    enabled: !valor && termo.length >= 2,
  });
  if (valor) {
    return (
      <Flex align="center" gap={2} p={2} border="1px solid #E2E8F0" borderRadius="8px">
        <Box flex={1} minW={0}>
          <Text fontSize="13px" fontWeight={600} noOfLines={1}>
            {valor.nome}
          </Text>
          <Text fontSize="11px" color="#64748B">
            ID {valor.external_id} · {cnpjFmt(valor.cnpj)}
          </Text>
        </Box>
        {!isDisabled && (
          <Button size="xs" variant="ghost" onClick={() => onChange(null)}>
            Trocar
          </Button>
        )}
      </Flex>
    );
  }
  return (
    <Box position="relative">
      <InputGroup size="sm">
        <InputLeftElement pointerEvents="none">
          {isFetching ? <Spinner size="xs" /> : <MagnifyingGlass size={14} color="#94A3B8" />}
        </InputLeftElement>
        <Input
          placeholder="Nome, CNPJ ou ID"
          value={texto}
          onChange={e => setTexto(e.target.value)}
          autoFocus
        />
      </InputGroup>
      {!!data?.items.length && (
        <List
          position="absolute"
          zIndex={10}
          bg="white"
          w="full"
          mt={1}
          border="1px solid #E2E8F0"
          borderRadius="8px"
          boxShadow="md"
          maxH="220px"
          overflowY="auto"
        >
          {data.items.map(i => (
            <ListItem
              key={i.empresa.id}
              px={3}
              py={2}
              cursor="pointer"
              _hover={{ bg: 'blue.50' }}
              onClick={() => onChange(i.empresa)}
            >
              <Text fontSize="13px">{i.empresa.nome}</Text>
              <Text fontSize="11px" color="#64748B">
                ID {i.empresa.external_id} · {cnpjFmt(i.empresa.cnpj)}
              </Text>
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
}

function FaturaModal({
  inicial,
  empresaInicial,
  competencia,
  onFechar,
}: {
  inicial: IFatura;
  empresaInicial: IEmpresaSn | null;
  competencia: string;
  onFechar: () => void;
}) {
  const qc = useQueryClient();
  const [f, setF] = useState<IFatura>(inicial);
  const [empresa, setEmpresa] = useState<IEmpresaSn | null>(empresaInicial);
  const set = (p: Partial<IFatura>) => setF(v => ({ ...v, ...p }));
  const servico = f.tipo === 'servico';
  const locacao = f.tipo === 'locacao';
  const produto = !servico && !locacao;
  const salvar = useMutation({
    mutationFn: () => {
      const body: IFatura = {
        ...f,
        empresa_id: empresa!.id,
        competencia,
        item_lc116: servico ? f.item_lc116 : null,
        iss_retido: servico && f.iss_retido,
        municipio_ibge: servico ? f.municipio_ibge : null,
        icms_st: produto && f.icms_st,
        monofasico: produto && f.monofasico,
      };
      delete body.empresa;
      return f.id ? snApi.editarFatura(f.id, body) : snApi.criarFatura(body);
    },
    onSuccess: () => {
      toast.success('Fatura salva e apuração recalculada.');
      qc.invalidateQueries({ queryKey: ['sn-faturas'] });
      qc.invalidateQueries({ queryKey: ['sn-lista'] });
      qc.invalidateQueries({ queryKey: ['sn-totais'] });
      onFechar();
    },
    onError: (e: any) => toast.error(mensagemErro(e)),
  });
  const valido = !!empresa && f.descricao.trim().length >= 2 && f.valor > 0 && !!f.data;
  return (
    <Modal isOpen onClose={onFechar} size="2xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="16px">{f.id ? 'Editar fatura' : 'Nova fatura'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl isRequired mb={3}>
            <FormLabel fontSize="12px" mb={1}>
              Empresa
            </FormLabel>
            <EmpresaBusca
              competencia={competencia}
              valor={empresa}
              onChange={setEmpresa}
              isDisabled={!!f.id}
            />
          </FormControl>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
            <FormControl isRequired>
              <FormLabel fontSize="12px" mb={1}>
                Tipo
              </FormLabel>
              <Select
                size="sm"
                value={f.tipo}
                onChange={e => set({ tipo: e.target.value as IFatura['tipo'] })}
              >
                {Object.entries(TIPOS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl isRequired>
              <FormLabel fontSize="12px" mb={1}>
                Data
              </FormLabel>
              <Input
                size="sm"
                type="date"
                value={f.data}
                onChange={e => set({ data: e.target.value })}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel fontSize="12px" mb={1}>
                Valor (R$)
              </FormLabel>
              <Input
                size="sm"
                type="number"
                step="0.01"
                min={0}
                value={f.valor || ''}
                onChange={e => set({ valor: Number(e.target.value) })}
              />
            </FormControl>
          </SimpleGrid>
          <FormControl isRequired mt={3}>
            <FormLabel fontSize="12px" mb={1}>
              Descrição
            </FormLabel>
            <Input
              size="sm"
              value={f.descricao}
              onChange={e => set({ descricao: e.target.value })}
            />
          </FormControl>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} mt={3}>
            <FormControl>
              <FormLabel fontSize="12px" mb={1}>
                Nº do documento
              </FormLabel>
              <Input
                size="sm"
                value={f.numero_documento ?? ''}
                onChange={e => set({ numero_documento: e.target.value || null })}
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="12px" mb={1}>
                CPF/CNPJ do tomador
              </FormLabel>
              <Input
                size="sm"
                value={f.tomador_doc ?? ''}
                onChange={e => set({ tomador_doc: e.target.value || null })}
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="12px" mb={1}>
                Nome do tomador
              </FormLabel>
              <Input
                size="sm"
                value={f.tomador_nome ?? ''}
                onChange={e => set({ tomador_nome: e.target.value || null })}
              />
            </FormControl>
          </SimpleGrid>
          {servico && (
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mt={3}>
              <FormControl>
                <FormLabel fontSize="12px" mb={1}>
                  Item da LC 116 (ex.: 7.02)
                </FormLabel>
                <Input
                  size="sm"
                  value={f.item_lc116 ?? ''}
                  onChange={e => set({ item_lc116: e.target.value || null })}
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="12px" mb={1}>
                  Município do serviço (IBGE, se outro)
                </FormLabel>
                <Input
                  size="sm"
                  value={f.municipio_ibge ?? ''}
                  onChange={e => set({ municipio_ibge: e.target.value || null })}
                />
              </FormControl>
              <Checkbox
                isChecked={f.iss_retido}
                onChange={e => set({ iss_retido: e.target.checked })}
              >
                ISS retido pelo tomador
              </Checkbox>
            </SimpleGrid>
          )}
          {locacao && (
            <Text mt={3} fontSize="12px" color="#64748B">
              Locação de bens móveis: entra no Anexo III sem incidência do ISSQN (o ISS sai do DAS).
              Não usa item da LC 116 nem Fator R.
            </Text>
          )}
          {produto && (
            <Flex gap={5} mt={3} wrap="wrap">
              <Checkbox isChecked={f.icms_st} onChange={e => set({ icms_st: e.target.checked })}>
                ICMS por substituição tributária
              </Checkbox>
              <Checkbox
                isChecked={f.monofasico}
                onChange={e => set({ monofasico: e.target.checked })}
              >
                PIS/COFINS monofásico
              </Checkbox>
            </Flex>
          )}
          <Checkbox
            mt={3}
            isChecked={f.exterior}
            onChange={e => set({ exterior: e.target.checked })}
          >
            Exportação / tomador no exterior
          </Checkbox>
          <FormControl mt={3}>
            <FormLabel fontSize="12px" mb={1}>
              Observação
            </FormLabel>
            <Textarea
              size="sm"
              value={f.observacao ?? ''}
              onChange={e => set({ observacao: e.target.value || null })}
            />
          </FormControl>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button size="sm" variant="ghost" onClick={onFechar}>
            Cancelar
          </Button>
          <Button
            size="sm"
            colorScheme="blue"
            isDisabled={!valido}
            isLoading={salvar.isPending}
            onClick={() => salvar.mutate()}
          >
            Salvar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

const COR_HEX: Record<IFatura['tipo'], string> = {
  servico: '#1B88F5',
  revenda: '#8B5CF6',
  industrializacao: '#A855F7',
  locacao: '#14B8A6',
};

/** Mesmo dia no mês da competência (31 vira o último dia de um mês curto). */
export function mesmaDataNoMes(data: string, competencia: string): string {
  const [a, m] = competencia.split('-').map(Number);
  const dia = Number(data.slice(8, 10)) || 1;
  const ultimo = new Date(a, m, 0).getDate();
  return `${a}-${String(m).padStart(2, '0')}-${String(Math.min(dia, ultimo)).padStart(2, '0')}`;
}

function csv(faturas: IFatura[]): string {
  const num = (v: number) => v.toFixed(2).replace('.', ',');
  const txt = (v?: string | null) => `"${(v ?? '').replace(/"/g, '""')}"`;
  const linhas = [
    [
      'Empresa',
      'ID',
      'CNPJ',
      'Data',
      'Tipo',
      'Item LC 116',
      'ISS retido',
      'Exterior',
      'Descrição',
      'Nº documento',
      'Tomador CPF/CNPJ',
      'Tomador',
      'Valor',
      'Lançado por',
    ].join(';'),
    ...faturas.map(f =>
      [
        txt(f.empresa?.nome),
        txt(f.empresa?.external_id),
        txt(cnpjFmt(f.empresa?.cnpj)),
        dataBr(f.data),
        TIPOS[f.tipo],
        txt(f.item_lc116),
        f.iss_retido ? 'Sim' : 'Não',
        f.exterior ? 'Sim' : 'Não',
        txt(f.descricao),
        txt(f.numero_documento),
        txt(f.tomador_doc),
        txt(f.tomador_nome),
        num(f.valor),
        txt(f.usuario_nome),
      ].join(';'),
    ),
  ];
  // BOM: o Excel abre o CSV em UTF-8 (acentos)
  return String.fromCharCode(0xfeff) + linhas.join('\r\n');
}

function baixarCsv(faturas: IFatura[], competencia: string) {
  const url = URL.createObjectURL(new Blob([csv(faturas)], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `faturas-simples-${competencia.slice(0, 7)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

const chaveFatura = (f: IFatura) => `${f.empresa_id}|${f.descricao}|${f.valor}`;

function Indicador({
  rotulo,
  valor,
  sub,
  cor,
  icone,
  dica,
  ativo,
  onClick,
}: {
  rotulo: string;
  valor: string;
  sub?: React.ReactNode;
  cor: string;
  icone: React.ReactNode;
  dica: string;
  ativo?: boolean;
  onClick?: () => void;
}) {
  const caixa = {
    textAlign: 'left' as const,
    w: 'full',
    p: '10px 12px',
    borderRadius: '14px',
    border: '1.5px solid',
    borderColor: ativo ? cor : 'rgba(15,23,42,0.07)',
    bg: ativo ? `${cor}14` : 'rgba(255,255,255,0.65)',
    transition: 'all 0.14s',
  };
  const conteudo = (
    <Flex align="center" gap="10px">
      <Flex
        w="32px"
        h="32px"
        borderRadius="10px"
        align="center"
        justify="center"
        flex="none"
        bg={`${cor}1A`}
        color={cor}
      >
        {icone}
      </Flex>
      <Box minW={0}>
        <Text fontSize="10.5px" color="#64748B" fontWeight={600} lineHeight="1.25">
          {rotulo}
        </Text>
        <Text
          fontSize="16px"
          fontWeight={800}
          color="#0F172A"
          lineHeight="1.25"
          sx={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {valor}
        </Text>
        {sub && (
          <Text as="div" fontSize="10.5px" color="#64748B" noOfLines={1}>
            {sub}
          </Text>
        )}
      </Box>
    </Flex>
  );
  return (
    <Tooltip label={dica} fontSize="11px" hasArrow placement="top" openDelay={300}>
      {onClick ? (
        <Box
          as="button"
          type="button"
          onClick={onClick}
          aria-pressed={!!ativo}
          {...caixa}
          _hover={{ borderColor: `${cor}66`, transform: 'translateY(-1px)' }}
          _focusVisible={{ outline: `2px solid ${cor}`, outlineOffset: '2px' }}
        >
          {conteudo}
        </Box>
      ) : (
        <Box {...caixa}>{conteudo}</Box>
      )}
    </Tooltip>
  );
}

/** Copia as faturas do mês anterior (receita recorrente: aluguel, mensalidade). */
function CopiarModal({
  competencia,
  anterior,
  faturas,
  jaLancadas,
  onFechar,
}: {
  competencia: string;
  anterior: string;
  faturas: IFatura[];
  jaLancadas: Set<string>;
  onFechar: () => void;
}) {
  const qc = useQueryClient();
  const [marcadas, setMarcadas] = useState<Set<number>>(
    () => new Set(faturas.filter(f => !jaLancadas.has(chaveFatura(f))).map(f => f.id as number)),
  );
  const [progresso, setProgresso] = useState<{
    feitas: number;
    total: number;
    erros: string[];
  } | null>(null);
  const rodando = !!progresso && progresso.feitas < progresso.total;

  const copiar = async () => {
    const alvo = faturas.filter(f => marcadas.has(f.id as number));
    const erros: string[] = [];
    setProgresso({ feitas: 0, total: alvo.length, erros });
    for (let i = 0; i < alvo.length; i += 1) {
      const f = alvo[i];
      // só os campos da fatura (sem id, empresa, situação e auditoria)
      const body: IFatura = {
        empresa_id: f.empresa_id,
        competencia,
        data: mesmaDataNoMes(f.data, competencia),
        tipo: f.tipo,
        descricao: f.descricao,
        numero_documento: f.numero_documento,
        tomador_doc: f.tomador_doc,
        tomador_nome: f.tomador_nome,
        valor: f.valor,
        item_lc116: f.item_lc116,
        iss_retido: f.iss_retido,
        municipio_ibge: f.municipio_ibge,
        icms_st: f.icms_st,
        monofasico: f.monofasico,
        exterior: f.exterior,
        observacao: f.observacao,
      };
      try {
        // uma por vez: cada fatura recalcula a apuração da empresa
        // eslint-disable-next-line no-await-in-loop
        await snApi.criarFatura(body);
      } catch (e: any) {
        erros.push(`${f.empresa?.nome ?? f.empresa_id}: ${mensagemErro(e)}`);
      }
      setProgresso({ feitas: i + 1, total: alvo.length, erros: [...erros] });
    }
    qc.invalidateQueries({ queryKey: ['sn-faturas'] });
    qc.invalidateQueries({ queryKey: ['sn-lista'] });
    qc.invalidateQueries({ queryKey: ['sn-totais'] });
    if (erros.length) {
      toast.warning(`${alvo.length - erros.length} copiada(s), ${erros.length} com erro.`);
    } else {
      toast.success(`${alvo.length} fatura(s) copiada(s) e apurações recalculadas.`);
      onFechar();
    }
  };
  const total = faturas.filter(f => marcadas.has(f.id as number)).reduce((s, f) => s + f.valor, 0);
  return (
    <Modal isOpen onClose={rodando ? () => undefined : onFechar} size="3xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="16px">
          Copiar faturas de {anterior.slice(5, 7)}/{anterior.slice(0, 4)}
          <Text fontSize="12px" color="#64748B" fontWeight={400}>
            Mesma empresa, descrição, tomador e valor; a data vira o mesmo dia deste mês. Confira o
            valor antes: aluguel com reajuste muda.
          </Text>
        </ModalHeader>
        {!rodando && <ModalCloseButton />}
        <ModalBody>
          {faturas.length === 0 ? (
            <Text fontSize="13px" color="#64748B" py={6} textAlign="center">
              Nenhuma fatura no mês anterior.
            </Text>
          ) : (
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th w="32px">
                    <Checkbox
                      isChecked={marcadas.size === faturas.length}
                      isIndeterminate={marcadas.size > 0 && marcadas.size < faturas.length}
                      onChange={e =>
                        setMarcadas(
                          e.target.checked ? new Set(faturas.map(f => f.id as number)) : new Set(),
                        )
                      }
                    />
                  </Th>
                  <Th>Empresa</Th>
                  <Th>Descrição</Th>
                  <Th isNumeric>Valor</Th>
                </Tr>
              </Thead>
              <Tbody>
                {faturas.map(f => {
                  const repetida = jaLancadas.has(chaveFatura(f));
                  return (
                    <Tr key={f.id} opacity={repetida ? 0.6 : 1}>
                      <Td>
                        <Checkbox
                          isChecked={marcadas.has(f.id as number)}
                          onChange={e =>
                            setMarcadas(atual => {
                              const novo = new Set(atual);
                              if (e.target.checked) novo.add(f.id as number);
                              else novo.delete(f.id as number);
                              return novo;
                            })
                          }
                        />
                      </Td>
                      <Td maxW="220px">
                        <Text noOfLines={1} fontSize="13px">
                          {f.empresa?.nome}
                        </Text>
                        {repetida && (
                          <Text fontSize="11px" color="#B45309">
                            Já lançada neste mês
                          </Text>
                        )}
                      </Td>
                      <Td maxW="260px">
                        <Text noOfLines={1} fontSize="13px">
                          {f.descricao}
                        </Text>
                        <Text fontSize="11px" color="#64748B">
                          {TIPOS[f.tipo]}
                          {f.tomador_nome ? ` · ${f.tomador_nome}` : ''}
                        </Text>
                      </Td>
                      <Td isNumeric fontWeight={600}>
                        {brl(f.valor)}
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          )}
          {progresso && (
            <Box mt={3}>
              <Progress
                size="sm"
                borderRadius="full"
                value={progresso.total ? (progresso.feitas / progresso.total) * 100 : 0}
                colorScheme={progresso.erros.length ? 'orange' : 'blue'}
              />
              {progresso.erros.map(e => (
                <Text key={e} fontSize="12px" color="#B91C1C" mt={1}>
                  {e}
                </Text>
              ))}
            </Box>
          )}
        </ModalBody>
        <ModalFooter gap={2}>
          <Text fontSize="12px" color="#64748B" mr="auto">
            {marcadas.size} marcada(s) · {brl(total)}
          </Text>
          <Button size="sm" variant="ghost" onClick={onFechar} isDisabled={rodando}>
            Fechar
          </Button>
          <Button
            size="sm"
            colorScheme="blue"
            leftIcon={<Copy size={15} />}
            isDisabled={!marcadas.size || (!!progresso && !rodando)}
            isLoading={rodando}
            onClick={copiar}
          >
            Copiar para {competencia.slice(5, 7)}/{competencia.slice(0, 4)}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function SituacaoMes({ f }: { f: IFatura }) {
  if (!f.situacao_apuracao) return null;
  const s = SITUACOES[f.situacao_apuracao];
  return (
    <Tooltip
      label={f.travada ? 'Mês transmitido: a fatura não muda (só por retificação).' : s.dica}
      fontSize="11px"
      hasArrow
    >
      <Flex align="center" gap={1} fontSize="11px" color={s.cor} fontWeight={600}>
        {f.travada && <Lock size={12} weight="bold" />}
        {s.rotulo}
      </Flex>
    </Tooltip>
  );
}

function Variacao({ atual, anterior }: { atual: number; anterior: number }) {
  if (anterior <= 0) return <>mês anterior {brl(anterior)}</>;
  const v = (atual - anterior) / anterior;
  let cor = '#64748B';
  if (v > 0) cor = '#16A34A';
  if (v < 0) cor = '#DC2626';
  return (
    <Flex as="span" align="center" gap="2px" color={cor} fontWeight={700}>
      {v >= 0 ? <ArrowUp size={10} weight="bold" /> : <ArrowDown size={10} weight="bold" />}
      {Math.abs(v * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}% vs {brl(anterior)}
    </Flex>
  );
}

export function FaturasSimplesPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const podeLancar = hasPermission(TELA_FATURAS_PATH, 'lancar');
  const tour = useTour('supervisor-simples-faturas');
  const { currentDate, handlePrevMonth, handleNextMonth, handleSelectMonth } = useMonthFilter();
  const competencia = currentDate.clone().startOf('month').format('YYYY-MM-DD');
  const anterior = currentDate.clone().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
  const [texto, setTexto] = useState('');
  const [busca] = useDebounceValue(texto, 400);
  const [tipo, setTipo] = useState<IFatura['tipo'] | ''>('');
  const [porEmpresa, setPorEmpresa] = useState(false);
  const [editando, setEditando] = useState<IFatura | null>(null);
  const [copiando, setCopiando] = useState(false);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['sn-faturas', competencia, busca],
    queryFn: () => snApi.faturas({ competencia, search: busca || undefined }),
  });
  const { data: dataAnterior } = useQuery({
    queryKey: ['sn-faturas', anterior, ''],
    queryFn: () => snApi.faturas({ competencia: anterior }),
  });
  const excluir = useMutation({
    mutationFn: (id: number) => snApi.excluirFatura(id),
    onSuccess: () => {
      toast.success('Fatura excluída e apuração recalculada.');
      qc.invalidateQueries({ queryKey: ['sn-faturas'] });
      qc.invalidateQueries({ queryKey: ['sn-lista'] });
      qc.invalidateQueries({ queryKey: ['sn-totais'] });
    },
    onError: (e: any) => toast.error(mensagemErro(e)),
  });

  const todas = useMemo(() => data?.items ?? [], [data]);
  const visiveis = useMemo(() => {
    if (!tipo) return todas;
    // "revenda" no KPI junta revenda e industrializado (os dois são mercadoria)
    if (tipo === 'revenda')
      return todas.filter(f => f.tipo === 'revenda' || f.tipo === 'industrializacao');
    return todas.filter(f => f.tipo === tipo);
  }, [todas, tipo]);
  const grupos = useMemo(() => {
    const m = new Map<number, IFatura[]>();
    visiveis.forEach(f => m.set(f.empresa_id, [...(m.get(f.empresa_id) ?? []), f]));
    return Array.from(m.values());
  }, [visiveis]);
  const jaLancadas = useMemo(() => new Set(todas.map(chaveFatura)), [todas]);
  const resumo = data?.resumo;
  const totalMes = data?.valor_total ?? 0;
  const travadas = todas.filter(f => f.travada).length;
  const qtdAnterior = dataAnterior?.items.length ?? 0;
  const porTipo = (t: IFatura['tipo']) => resumo?.por_tipo?.[t];
  const produtos = (porTipo('revenda')?.valor ?? 0) + (porTipo('industrializacao')?.valor ?? 0);
  const qtdProdutos =
    (porTipo('revenda')?.quantidade ?? 0) + (porTipo('industrializacao')?.quantidade ?? 0);
  const filtrarTipo = (t: IFatura['tipo']) => setTipo(tipo === t ? '' : t);

  const linha = (f: IFatura, mostrarEmpresa: boolean) => (
    <Tr key={f.id} _hover={{ bg: 'rgba(27,136,245,0.04)' }}>
      {mostrarEmpresa && (
        <Td maxW="220px">
          <Text noOfLines={1} fontSize="13px" fontWeight={600}>
            {f.empresa?.nome}
          </Text>
          <Flex gap={2} align="center">
            <Text fontSize="11px" color="#64748B">
              ID {f.empresa?.external_id}
            </Text>
            <SituacaoMes f={f} />
          </Flex>
        </Td>
      )}
      <Td fontSize="13px">{dataBr(f.data)}</Td>
      <Td>
        <Badge variant="subtle" colorScheme={COR_TIPO[f.tipo]}>
          {TIPOS[f.tipo]}
        </Badge>
        {f.item_lc116 && (
          <Text fontSize="11px" color="#64748B">
            LC 116 {f.item_lc116}
            {f.iss_retido ? ' · ISS retido' : ''}
          </Text>
        )}
        {(f.icms_st || f.monofasico || f.exterior) && (
          <Text fontSize="11px" color="#64748B">
            {[f.icms_st && 'ST', f.monofasico && 'monofásico', f.exterior && 'exterior']
              .filter(Boolean)
              .join(' · ')}
          </Text>
        )}
      </Td>
      <Td maxW="260px">
        <Text noOfLines={2} fontSize="13px">
          {f.descricao}
        </Text>
        {f.numero_documento && (
          <Text fontSize="11px" color="#64748B">
            Nº {f.numero_documento}
          </Text>
        )}
      </Td>
      <Td maxW="180px">
        <Text noOfLines={1} fontSize="13px">
          {f.tomador_nome ?? '—'}
        </Text>
        {f.tomador_doc && (
          <Text fontSize="11px" color="#64748B">
            {cnpjFmt(f.tomador_doc)}
          </Text>
        )}
      </Td>
      <Td isNumeric fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {brl(f.valor)}
      </Td>
      <Td fontSize="12px" color="#64748B">
        {f.usuario_nome}
      </Td>
      <Td>
        {podeLancar && f.travada && (
          <Tooltip label="Mês transmitido: só por retificação" fontSize="11px">
            <Flex color="#94A3B8" justify="center">
              <Lock size={15} />
            </Flex>
          </Tooltip>
        )}
        {podeLancar && !f.travada && (
          <Flex gap={1}>
            <Tooltip label="Editar" fontSize="11px">
              <IconButton
                aria-label="Editar"
                size="xs"
                variant="ghost"
                icon={<PencilSimple size={14} />}
                onClick={() => setEditando(f)}
              />
            </Tooltip>
            <Tooltip label="Excluir" fontSize="11px">
              <IconButton
                aria-label="Excluir"
                size="xs"
                variant="ghost"
                colorScheme="red"
                icon={<Trash size={14} />}
                isLoading={excluir.isPending && excluir.variables === f.id}
                onClick={() => {
                  // eslint-disable-next-line no-alert
                  if (window.confirm('Excluir esta fatura? O mês será recalculado.')) {
                    excluir.mutate(f.id as number);
                  }
                }}
              />
            </Tooltip>
          </Flex>
        )}
      </Td>
    </Tr>
  );

  return (
    <GlassPageBg>
      <Flex direction="column" gap={5} px={{ base: 4, md: 6 }} py={{ base: 4, md: 6 }}>
        <Flex align="center" gap={3} wrap="wrap">
          <ReturnButton previousPage="modulesHome/supervisor/simples" />
          <Flex
            w="46px"
            h="46px"
            borderRadius="14px"
            align="center"
            justify="center"
            flex="none"
            bgGradient="linear(135deg, #1B88F5, #6366F1)"
            color="white"
            boxShadow="0 8px 20px -6px rgba(99,102,241,0.45)"
          >
            <FileText size={24} weight="duotone" />
          </Flex>
          <Box minW={0}>
            <Text
              fontSize="11px"
              letterSpacing="0.14em"
              textTransform="uppercase"
              color="#94A3B8"
              fontWeight={600}
            >
              Supervisor › Simples Nacional
            </Text>
            <Text fontSize="22px" fontWeight={700} letterSpacing="-0.02em" color="#0F172A">
              Faturas (receita sem nota)
            </Text>
          </Box>
          <Box flex={1} />
          {isFetching && <Spinner size="sm" color="#1B88F5" />}
          <Button size="sm" variant="ghost" leftIcon={<Question size={16} />} onClick={tour.abrir}>
            Como funciona
          </Button>
          <Box data-tour="mes">
            <MonthNavigator
              currentDate={currentDate}
              onPrevMonth={handlePrevMonth}
              onNextMonth={handleNextMonth}
              onSelectMonth={handleSelectMonth}
              compact
            />
          </Box>
        </Flex>

        <GlassCard intensity="strong" p="14px 16px" data-tour="kpis">
          <SimpleGrid columns={{ base: 2, md: 4, '2xl': 7 }} spacing={2}>
            <Indicador
              rotulo="Total lançado no mês"
              valor={brl(totalMes)}
              sub={<Variacao atual={totalMes} anterior={dataAnterior?.valor_total ?? 0} />}
              cor="#1B88F5"
              icone={<CurrencyCircleDollar size={18} weight="duotone" />}
              dica="Soma das faturas do mês, comparada com o mês anterior. Clique para ver todos os tipos"
              ativo={!tipo}
              onClick={() => setTipo('')}
            />
            <Indicador
              rotulo="Faturas"
              valor={(data?.total ?? 0).toLocaleString('pt-BR')}
              sub={`${resumo?.empresas ?? 0} empresa(s)${travadas ? ` · ${travadas} travada(s)` : ''}`}
              cor="#6366F1"
              icone={<ListBullets size={18} weight="duotone" />}
              dica="Quantidade de faturas e de empresas com fatura no mês. Travada = mês já transmitido"
            />
            <Indicador
              rotulo={TIPOS.servico}
              valor={brl(porTipo('servico')?.valor ?? 0)}
              sub={`${porTipo('servico')?.quantidade ?? 0} fatura(s)`}
              cor={COR_HEX.servico}
              icone={<Briefcase size={18} weight="duotone" />}
              dica="Serviço: anexo pelo item da LC 116 (Fator R quando couber). Clique para filtrar"
              ativo={tipo === 'servico'}
              onClick={() => filtrarTipo('servico')}
            />
            <Indicador
              rotulo="Mercadoria"
              valor={brl(produtos)}
              sub={`${qtdProdutos} fatura(s) · revenda e industrializado`}
              cor={COR_HEX.revenda}
              icone={<Package size={18} weight="duotone" />}
              dica="Revenda (Anexo I) e venda de industrializado (Anexo II). Clique para filtrar"
              ativo={tipo === 'revenda'}
              onClick={() => filtrarTipo('revenda')}
            />
            <Indicador
              rotulo={TIPOS.locacao}
              valor={brl(porTipo('locacao')?.valor ?? 0)}
              sub={`${porTipo('locacao')?.quantidade ?? 0} fatura(s)`}
              cor={COR_HEX.locacao}
              icone={<Key size={18} weight="duotone" />}
              dica="Locação de bens móveis: Anexo III sem ISS. Clique para filtrar"
              ativo={tipo === 'locacao'}
              onClick={() => filtrarTipo('locacao')}
            />
            <Indicador
              rotulo="ISS retido"
              valor={brl(resumo?.iss_retido ?? 0)}
              cor="#F59E0B"
              icone={<Coins size={18} weight="duotone" />}
              dica="Valor das faturas com ISS retido pelo tomador: o ISS sai do DAS"
            />
            <Indicador
              rotulo="Exportação"
              valor={brl(resumo?.exterior ?? 0)}
              cor="#0EA5E9"
              icone={<Globe size={18} weight="duotone" />}
              dica="Tomador no exterior: mercado externo na declaração"
            />
          </SimpleGrid>
        </GlassCard>

        <GlassCard intensity="strong" p="12px 14px" overflow="visible">
          <Flex gap={3} wrap="wrap" align="center">
            <InputGroup size="sm" w={{ base: 'full', md: '300px' }}>
              <InputLeftElement pointerEvents="none">
                <MagnifyingGlass size={15} color="#94A3B8" />
              </InputLeftElement>
              <Input
                borderRadius="10px"
                placeholder="Empresa, descrição, tomador, nº"
                value={texto}
                onChange={e => setTexto(e.target.value)}
              />
            </InputGroup>
            <Select
              size="sm"
              borderRadius="10px"
              w={{ base: 'full', md: '210px' }}
              value={tipo}
              onChange={e => setTipo(e.target.value as IFatura['tipo'] | '')}
              aria-label="Filtrar por tipo"
            >
              <option value="">Todos os tipos</option>
              {Object.entries(TIPOS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </Select>
            <ButtonGroup size="sm" isAttached variant="outline">
              <Button
                leftIcon={<Rows size={14} />}
                colorScheme={porEmpresa ? 'gray' : 'blue'}
                onClick={() => setPorEmpresa(false)}
                aria-pressed={!porEmpresa}
              >
                Lista
              </Button>
              <Button
                leftIcon={<Buildings size={14} />}
                colorScheme={porEmpresa ? 'blue' : 'gray'}
                onClick={() => setPorEmpresa(true)}
                aria-pressed={porEmpresa}
              >
                Por empresa
              </Button>
            </ButtonGroup>
            <Text fontSize="12px" color="#64748B" fontWeight={600}>
              {visiveis.length} de {todas.length} · {brl(visiveis.reduce((s, f) => s + f.valor, 0))}
            </Text>
            <Box flex={1} />
            <Button
              size="sm"
              variant="ghost"
              borderRadius="10px"
              leftIcon={<DownloadSimple size={15} />}
              isDisabled={!visiveis.length}
              onClick={() => baixarCsv(visiveis, competencia)}
            >
              Exportar CSV
            </Button>
            {podeLancar && (
              <Tooltip
                label="Lança de novo neste mês as faturas recorrentes do mês anterior (aluguel, mensalidade)"
                fontSize="11px"
                hasArrow
              >
                <Button
                  size="sm"
                  variant="outline"
                  borderRadius="10px"
                  leftIcon={<Copy size={15} />}
                  isDisabled={!qtdAnterior}
                  onClick={() => setCopiando(true)}
                  data-tour="copiar"
                >
                  Copiar do mês anterior{qtdAnterior ? ` (${qtdAnterior})` : ''}
                </Button>
              </Tooltip>
            )}
            {podeLancar && (
              <Button
                size="sm"
                colorScheme="blue"
                borderRadius="10px"
                leftIcon={<Plus size={15} />}
                onClick={() => setEditando(VAZIA(0, competencia))}
                data-tour="nova"
              >
                Nova fatura
              </Button>
            )}
          </Flex>
        </GlassCard>

        <GlassCard intensity="strong" p={0} data-tour="lista">
          {isLoading ? (
            <Flex justify="center" py={10}>
              <Spinner color="#1B88F5" />
            </Flex>
          ) : (
            <Box overflowX="auto">
              <Table size="sm">
                <Thead>
                  <Tr>
                    {!porEmpresa && <Th>Empresa</Th>}
                    <Th>Data</Th>
                    <Th data-tour="tipo">Tipo</Th>
                    <Th>Descrição</Th>
                    <Th>Tomador</Th>
                    <Th isNumeric>Valor</Th>
                    <Th>Lançado por</Th>
                    <Th />
                  </Tr>
                </Thead>
                <Tbody>
                  {visiveis.length === 0 && (
                    <Tr>
                      <Td colSpan={8} py={10}>
                        <Flex direction="column" align="center" gap={2} color="#64748B">
                          <FileText size={36} color="#CBD5E1" />
                          <Text fontSize="13px">
                            {todas.length
                              ? 'Nenhuma fatura com esse filtro.'
                              : 'Nenhuma fatura neste mês.'}
                          </Text>
                          {!todas.length && podeLancar && qtdAnterior > 0 && (
                            <Button
                              size="xs"
                              variant="outline"
                              leftIcon={<Copy size={13} />}
                              onClick={() => setCopiando(true)}
                            >
                              Copiar as {qtdAnterior} do mês anterior
                            </Button>
                          )}
                        </Flex>
                      </Td>
                    </Tr>
                  )}
                  {!porEmpresa && visiveis.map(f => linha(f, true))}
                  {porEmpresa &&
                    grupos.map(g => {
                      const e = g[0].empresa;
                      const subtotal = g.reduce((s, f) => s + f.valor, 0);
                      return (
                        <React.Fragment key={g[0].empresa_id}>
                          <Tr bg="rgba(27,136,245,0.06)">
                            <Td colSpan={4}>
                              <Flex align="center" gap={3} wrap="wrap">
                                <Text fontSize="13px" fontWeight={700}>
                                  {e?.nome}
                                </Text>
                                <Text fontSize="11px" color="#64748B">
                                  ID {e?.external_id} · {cnpjFmt(e?.cnpj)} · {g.length} fatura(s)
                                </Text>
                                <SituacaoMes f={g[0]} />
                              </Flex>
                            </Td>
                            <Td
                              isNumeric
                              fontWeight={800}
                              sx={{ fontVariantNumeric: 'tabular-nums' }}
                            >
                              {brl(subtotal)}
                            </Td>
                            <Td colSpan={2}>
                              {podeLancar && !g[0].travada && e && (
                                <Button
                                  size="xs"
                                  variant="ghost"
                                  leftIcon={<Plus size={12} />}
                                  onClick={() =>
                                    setEditando({ ...VAZIA(e.id, competencia), empresa: e })
                                  }
                                >
                                  Lançar
                                </Button>
                              )}
                            </Td>
                          </Tr>
                          {g.map(f => linha(f, false))}
                        </React.Fragment>
                      );
                    })}
                </Tbody>
              </Table>
            </Box>
          )}
        </GlassCard>
      </Flex>
      {editando && (
        <FaturaModal
          inicial={editando}
          empresaInicial={editando.empresa ?? null}
          competencia={competencia}
          onFechar={() => setEditando(null)}
        />
      )}
      {copiando && (
        <CopiarModal
          competencia={competencia}
          anterior={anterior}
          faturas={dataAnterior?.items ?? []}
          jaLancadas={jaLancadas}
          onFechar={() => setCopiando(false)}
        />
      )}
      <Tour passos={PASSOS} aberto={tour.aberto} onFechar={tour.fechar} />
    </GlassPageBg>
  );
}
